import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  DatePicker,
  Spin,
  message,
  Input,
  Divider,
  Tag,
  Empty,
  Tooltip,
  Select,
  Space,
} from 'antd';
import {
  CalendarOutlined,
  ReloadOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  GoogleOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  EditOutlined,
  MailOutlined,
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
  CloseOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import jsPDF from 'jspdf';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';

import { colors, spacing, radius, typography, shadows, transitions } from '../../../styles/monochromeTheme';
import { getFormattedEventsForDate, getDaySummary, getCalendarList } from '../../../api/googleCalendar';
import { isGoogleAuthenticated, getGoogleAuthUrl, clearTokens } from '../../../api/googleDrive';
import { searchClientThreads } from '../../../api/gmail';
import { getEmployees } from '../../../api/supabase';

dayjs.locale('fr');

const { TextArea } = Input;

// Styles
const styles = {
  page: {
    padding: spacing.xxxxl,
    minHeight: '100vh',
    background: colors.bgPrimary,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xxl,
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    boxShadow: shadows.md,
    border: `1px solid ${colors.borderLight}`,
    padding: `${spacing.lg} ${spacing.xxl}`,
  },
  headerIcon: {
    width: '48px',
    height: '48px',
    borderRadius: radius.xxl,
    background: colors.bgDark,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    color: colors.textInverse,
  },
  headerTitle: {
    margin: 0,
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
  },
  mainContainer: {
    display: 'grid',
    gridTemplateColumns: '320px 1fr',
    gap: spacing.xxl,
    alignItems: 'start',
  },
  sidebar: {
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    boxShadow: shadows.md,
    border: `1px solid ${colors.borderLight}`,
    padding: spacing.xxl,
    position: 'sticky',
    top: spacing.xxl,
  },
  sidebarTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    marginBottom: spacing.lg,
    color: colors.textPrimary,
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: `${spacing.sm} 0`,
    borderBottom: `1px solid ${colors.borderLight}`,
  },
  statLabel: {
    color: colors.textMuted,
    fontSize: typography.fontSize.sm,
  },
  statValue: {
    color: colors.textPrimary,
    fontWeight: typography.fontWeight.semibold,
    fontSize: typography.fontSize.sm,
  },
  eventTypeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.xs,
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: radius.md,
    background: colors.bgTertiary,
    fontSize: typography.fontSize.xs,
    marginRight: spacing.xs,
    marginBottom: spacing.xs,
  },
  mainContent: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: spacing.lg,
  },
  eventCard: {
    background: colors.bgSecondary,
    borderRadius: radius.xl,
    boxShadow: shadows.sm,
    border: `1px solid ${colors.borderLight}`,
    overflow: 'hidden',
    transition: transitions.normal,
  },
  eventHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.lg,
    borderBottom: `1px solid ${colors.borderLight}`,
    background: colors.bgTertiary,
  },
  eventTime: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    color: colors.textPrimary,
    fontWeight: typography.fontWeight.semibold,
    fontSize: typography.fontSize.md,
  },
  eventTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
    margin: 0,
  },
  eventBody: {
    padding: spacing.lg,
  },
  eventDescription: {
    color: colors.textSecondary,
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.md,
    whiteSpace: 'pre-wrap',
  },
  eventLocation: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    color: colors.textMuted,
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.md,
  },
  notesSection: {
    marginTop: spacing.md,
    padding: spacing.md,
    background: colors.bgTertiary,
    borderRadius: radius.lg,
    border: `1px dashed ${colors.borderMedium}`,
  },
  notesLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textMuted,
    marginBottom: spacing.sm,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
  },
  primaryBtn: {
    height: '40px',
    borderRadius: radius.lg,
    background: colors.bgDark,
    border: 'none',
    color: colors.textInverse,
    fontWeight: typography.fontWeight.semibold,
  },
  secondaryBtn: {
    height: '40px',
    borderRadius: radius.lg,
    background: colors.bgSecondary,
    border: `1px solid ${colors.borderMedium}`,
    color: colors.textSecondary,
    fontWeight: typography.fontWeight.medium,
  },
  exportButtons: {
    display: 'flex',
    gap: spacing.md,
    marginTop: spacing.lg,
  },
  // Review mode styles
  reviewContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing.xxl,
    alignItems: 'start',
  },
  reviewCard: {
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    boxShadow: shadows.md,
    border: `2px solid ${colors.bgDark}`,
    overflow: 'hidden',
  },
  reviewNavigation: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.lg,
    background: colors.bgTertiary,
    borderBottom: `1px solid ${colors.borderLight}`,
  },
  reviewCounter: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
  },
  emailPanel: {
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    boxShadow: shadows.md,
    border: `1px solid ${colors.borderLight}`,
    overflow: 'hidden',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
  },
  emailHeader: {
    padding: spacing.lg,
    background: colors.bgTertiary,
    borderBottom: `1px solid ${colors.borderLight}`,
  },
  emailSearchBox: {
    display: 'flex',
    gap: spacing.md,
    marginTop: spacing.md,
  },
  threadList: {
    flex: 1,
    overflowY: 'auto',
    maxHeight: '300px',
  },
  threadItem: {
    padding: spacing.md,
    borderBottom: `1px solid ${colors.borderLight}`,
    cursor: 'pointer',
    transition: transitions.fast,
  },
  threadItemActive: {
    background: colors.bgTertiary,
  },
  threadSubject: {
    fontWeight: typography.fontWeight.semibold,
    fontSize: typography.fontSize.sm,
    color: colors.textPrimary,
    marginBottom: spacing.xs,
  },
  threadSnippet: {
    fontSize: typography.fontSize.xs,
    color: colors.textMuted,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  threadViewer: {
    flex: 1,
    overflowY: 'auto',
    padding: spacing.lg,
    background: colors.bgPrimary,
    maxHeight: '400px',
  },
  emailMessage: {
    marginBottom: spacing.lg,
    padding: spacing.md,
    background: colors.bgSecondary,
    borderRadius: radius.lg,
    border: `1px solid ${colors.borderLight}`,
  },
  emailFrom: {
    fontWeight: typography.fontWeight.semibold,
    fontSize: typography.fontSize.sm,
    color: colors.textPrimary,
  },
  emailDate: {
    fontSize: typography.fontSize.xs,
    color: colors.textMuted,
  },
  emailBody: {
    marginTop: spacing.sm,
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  // Crew panel styles
  crewPanel: {
    background: colors.bgSecondary,
    borderRadius: radius.xl,
    padding: spacing.lg,
    marginBottom: spacing.lg,
    border: `1px solid ${colors.borderLight}`,
  },
  crewPanelTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
    marginBottom: spacing.md,
    color: colors.textPrimary,
  },
  crewChip: {
    display: 'inline-block',
    padding: `${spacing.xs} ${spacing.sm}`,
    margin: spacing.xs,
    background: colors.bgTertiary,
    borderRadius: radius.md,
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    border: `1px solid ${colors.borderLight}`,
  },
  crewSelector: {
    marginBottom: spacing.md,
    padding: spacing.md,
    background: colors.bgTertiary,
    borderRadius: radius.lg,
  },
  crewSelectorLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textMuted,
    marginBottom: spacing.xs,
    display: 'block',
  },
  transferSection: {
    marginBottom: spacing.md,
    padding: spacing.md,
    background: '#f5f0ff',
    borderRadius: radius.lg,
    border: `1px dashed #9c7bea`,
  },
  transferSectionLabel: {
    fontSize: typography.fontSize.xs,
    color: '#7b1fa2',
    marginBottom: spacing.sm,
    fontWeight: typography.fontWeight.semibold,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
  },
  transferField: {
    marginBottom: spacing.sm,
  },
  transferFieldLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.textMuted,
    marginBottom: spacing.xs,
    display: 'block',
  },
};

// Event type colors based on Google Calendar colors
const eventTypeColors = {
  'full_day_sbh': { border: '#F4511E', bg: '#fbe9e7', color: '#fff' },         // Orange
  'full_day_sxm': { border: '#F28B82', bg: '#ffebee', color: '#fff' },         // Rose/Saumon
  'full_day_axa': { border: '#0B8043', bg: '#e8f5e9', color: '#fff' },         // Vert foncé
  'half_day_aprem': { border: '#33B679', bg: '#e8f5e9', color: '#fff' },       // Vert clair
  'half_day_matin': { border: '#A142F4', bg: '#f3e5f5', color: '#fff' },       // Violet
  'trf_sbh_sxm': { border: '#FDD663', bg: '#fffde7', color: '#333' },          // Jaune
  'trf_sxm_sbh': { border: '#D93025', bg: '#ffebee', color: '#fff' },          // Rouge
  'trf_axa': { border: '#039BE5', bg: '#e3f2fd', color: '#fff' },              // Bleu
  'option': { border: '#8AB4F8', bg: '#e8eaf6', color: '#fff' },               // Bleu lavande
  'sunset': { border: '#9AA0A6', bg: '#fafafa', color: '#fff' },               // Gris
  'Charter': { border: '#1976d2', bg: '#e3f2fd', color: '#fff' },
  'Lunch': { border: '#f57c00', bg: '#fff3e0', color: '#fff' },
  'Transfert': { border: '#7b1fa2', bg: '#f3e5f5', color: '#fff' },
  'Seabob': { border: '#00838f', bg: '#e0f7fa', color: '#fff' },
  'Jet-ski': { border: '#c2185b', bg: '#fce4ec', color: '#fff' },
  'Autre': { border: colors.borderMedium, bg: colors.bgTertiary, color: colors.textSecondary },
};

// Get event color type from title (matches Google Calendar naming)
const getEventColorType = (title) => {
  const t = title.toLowerCase();

  // Transfer types (check first - more specific)
  // Detect patterns like: TSF SXM TO SBH, SXM/SBH, SXM-SBH, SXM → SBH
  const isTransfer = t.includes('trf') || t.includes('tsf') || t.includes('transfert') || t.includes('transfer');
  const hasSxmToSbh = t.includes('sxm to sbh') || t.includes('sxm/sbh') || t.includes('sxm-sbh') || t.includes('sxm → sbh');
  const hasSbhToSxm = t.includes('sbh to sxm') || t.includes('sbh/sxm') || t.includes('sbh-sxm') || t.includes('sbh → sxm');
  const hasAxa = t.includes('axa');

  if (isTransfer || hasSxmToSbh || hasSbhToSxm) {
    if (hasSbhToSxm) return 'trf_sbh_sxm';
    if (hasSxmToSbh) return 'trf_sxm_sbh';
    if (hasAxa) return 'trf_axa';
    return 'Transfert';
  }

  // Full day types
  if (t.includes('full day') || t.includes('fullday')) {
    if (t.includes('sbh')) return 'full_day_sbh';
    if (t.includes('sxm')) return 'full_day_sxm';
    if (t.includes('axa')) return 'full_day_axa';
  }

  // Half day types
  if (t.includes('half day') || t.includes('halfday')) {
    if (t.includes('aprem') || t.includes('après') || t.includes('pm')) return 'half_day_aprem';
    if (t.includes('matin') || t.includes('am')) return 'half_day_matin';
    return 'half_day_aprem'; // default
  }

  // Sunset
  if (t.includes('sunset')) return 'sunset';

  // Option
  if (t.includes('option')) return 'option';

  // Other types
  if (t.includes('charter') || t.includes('yacht') || t.includes('bateau')) return 'Charter';
  if (t.includes('lunch') || t.includes('dejeuner') || t.includes('déjeuner')) return 'Lunch';
  if (t.includes('seabob')) return 'Seabob';
  if (t.includes('jetski') || t.includes('jet-ski') || t.includes('jet ski')) return 'Jet-ski';

  return 'Autre';
};

function AgendaDayEditor() {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState('primary');
  const [eventNotes, setEventNotes] = useState({});
  const [exporting, setExporting] = useState(false);

  // Review mode state
  const [reviewMode, setReviewMode] = useState(false);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [emailSearchQuery, setEmailSearchQuery] = useState('');
  const [emailThreads, setEmailThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [searchingEmails, setSearchingEmails] = useState(false);

  // Crew assignment state
  const [crewMembers, setCrewMembers] = useState([]);
  const [eventCrewAssignments, setEventCrewAssignments] = useState({}); // { eventId: [employeeId, ...] }

  // Transfer details state
  const [eventTransferDetails, setEventTransferDetails] = useState({}); // { eventId: { hotel, transport, flightTime, flightNumber } }

  // Check authentication on mount
  useEffect(() => {
    const isAuth = isGoogleAuthenticated();
    setAuthenticated(isAuth);
    if (isAuth) {
      loadCalendars();
    }
  }, []);

  // Load events when date or calendar changes
  useEffect(() => {
    if (authenticated && selectedDate) {
      loadEvents();
    }
  }, [selectedDate, selectedCalendar, authenticated]);

  // Load crew members on mount
  useEffect(() => {
    loadCrewMembers();
  }, []);

  const loadCrewMembers = async () => {
    try {
      const { data: employees, error } = await getEmployees();
      if (error || !employees) {
        console.error('Error loading employees:', error);
        return;
      }
      // Filter only active sailors and captains
      const crew = employees.filter(emp => {
        const role = (emp.role || '').toLowerCase();
        const isActive = emp.status !== 'inactive';
        const isCrew = role.includes('capitaine') || role.includes('marin') || role.includes('skipper');
        return isActive && isCrew;
      });
      // Sort: captains first, then sailors
      crew.sort((a, b) => {
        const roleA = (a.role || '').toLowerCase();
        const roleB = (b.role || '').toLowerCase();
        const aIsCaptain = roleA.includes('capitaine') || roleA.includes('skipper');
        const bIsCaptain = roleB.includes('capitaine') || roleB.includes('skipper');
        if (aIsCaptain && !bIsCaptain) return -1;
        if (!aIsCaptain && bIsCaptain) return 1;
        return 0;
      });
      setCrewMembers(crew);
    } catch (error) {
      console.error('Error loading crew members:', error);
    }
  };

  const loadCalendars = async () => {
    try {
      console.log('Loading calendars...');
      const calendarList = await getCalendarList();
      console.log('Calendars loaded:', calendarList);
      setCalendars(calendarList);

      // If we have calendars, log them for debugging
      if (calendarList.length > 0) {
        console.log('Available calendars:');
        calendarList.forEach(cal => {
          console.log(`  - ${cal.summary} (ID: ${cal.id})`);
        });
      }
    } catch (error) {
      console.error('Error loading calendars:', error);
      message.error(`Erreur calendriers: ${error.message}`);
      if (error.message?.startsWith('NOT_AUTHENTICATED')) {
        setAuthenticated(false);
      }
    }
  };

  const loadEvents = async () => {
    setLoading(true);
    try {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      console.log(`Loading events for calendar: ${selectedCalendar}, date: ${dateStr}`);
      const fetchedEvents = await getFormattedEventsForDate(selectedCalendar, dateStr);
      console.log(`Loaded ${fetchedEvents.length} events:`, fetchedEvents);
      setEvents(fetchedEvents);

      // Initialize notes for new events
      const newNotes = { ...eventNotes };
      fetchedEvents.forEach(event => {
        if (!newNotes[event.id]) {
          newNotes[event.id] = '';
        }
      });
      setEventNotes(newNotes);
    } catch (error) {
      console.error('Error loading events:', error);
      message.error(`Erreur: ${error.message || 'Erreur lors du chargement des événements'}`);
      if (error.message?.startsWith('NOT_AUTHENTICATED') || error.message?.includes('401') || error.message?.includes('invalid_grant')) {
        setAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNoteChange = (eventId, value) => {
    setEventNotes(prev => ({
      ...prev,
      [eventId]: value,
    }));
  };

  const handleCrewAssignment = (eventId, crewIds) => {
    setEventCrewAssignments(prev => ({
      ...prev,
      [eventId]: crewIds,
    }));
  };

  // Get crew members assigned to any event
  const getAssignedCrewIds = () => {
    const assigned = new Set();
    Object.values(eventCrewAssignments).forEach(crewIds => {
      (crewIds || []).forEach(id => assigned.add(id));
    });
    return assigned;
  };

  // Get remaining available crew (not assigned to any event)
  const getAvailableCrew = () => {
    const assignedIds = getAssignedCrewIds();
    return crewMembers.filter(crew => !assignedIds.has(crew.id));
  };

  // Get crew name by ID
  const getCrewName = (crewId) => {
    const crew = crewMembers.find(c => c.id === crewId);
    return crew ? (crew.first_name || crew.name) : '';
  };

  // Get suggested default crew (1 captain + 2 sailors)
  const getSuggestedCrew = () => {
    const captains = crewMembers.filter(c => {
      const role = (c.role || '').toLowerCase();
      return role.includes('capitaine') || role.includes('skipper');
    });
    const sailors = crewMembers.filter(c => {
      const role = (c.role || '').toLowerCase();
      return !role.includes('capitaine') && !role.includes('skipper');
    });

    const suggested = [];
    // Add first captain
    if (captains.length > 0) suggested.push(captains[0]);
    // Add first 2 sailors
    if (sailors.length > 0) suggested.push(sailors[0]);
    if (sailors.length > 1) suggested.push(sailors[1]);

    return suggested;
  };

  // Get remaining crew (not in suggested list)
  const getRemainingCrew = () => {
    const suggested = getSuggestedCrew();
    const suggestedIds = new Set(suggested.map(c => c.id));
    return crewMembers.filter(c => !suggestedIds.has(c.id));
  };

  // Check if event is a transfer type (SXM/SBH, SBH/SXM, AXA/SBH, etc.)
  const isTransferEvent = (title) => {
    const t = title.toLowerCase();
    // Detect patterns: sxm/sbh, sxm to sbh, tsf, trf, etc.
    const hasTransferKeyword = t.includes('trf') || t.includes('tsf') || t.includes('transfert') || t.includes('transfer');
    const transferPatterns = [
      'sxm/sbh', 'sbh/sxm', 'axa/sbh', 'sbh/axa', 'sxm/axa', 'axa/sxm',
      'sxm to sbh', 'sbh to sxm', 'axa to sbh', 'sbh to axa', 'sxm to axa', 'axa to sxm',
      'sxm-sbh', 'sbh-sxm', 'axa-sbh', 'sbh-axa'
    ];
    return hasTransferKeyword || transferPatterns.some(pattern => t.includes(pattern));
  };

  // Check if transfer involves SXM (for VIP Georges hint)
  const isSXMTransfer = (title) => {
    const t = title.toLowerCase();
    return t.includes('sxm/') || t.includes('/sxm') || t.includes('sxm to') || t.includes('to sxm');
  };

  // Handle transfer detail changes
  const handleTransferDetailChange = (eventId, field, value) => {
    setEventTransferDetails(prev => ({
      ...prev,
      [eventId]: {
        ...(prev[eventId] || {}),
        [field]: value,
      },
    }));
  };

  const handleConnectGoogle = () => {
    const authUrl = getGoogleAuthUrl();
    window.location.href = authUrl;
  };

  // Review mode functions
  const enterReviewMode = () => {
    setReviewMode(true);
    setCurrentEventIndex(0);
    setEmailThreads([]);
    setSelectedThread(null);
    // Auto-search for first event
    if (events.length > 0) {
      const clientName = extractClientName(events[0].title);
      setEmailSearchQuery(clientName);
    }
  };

  const exitReviewMode = () => {
    setReviewMode(false);
    setEmailThreads([]);
    setSelectedThread(null);
  };

  const goToNextEvent = () => {
    if (currentEventIndex < events.length - 1) {
      const newIndex = currentEventIndex + 1;
      setCurrentEventIndex(newIndex);
      setEmailThreads([]);
      setSelectedThread(null);
      // Auto-search for new event
      const clientName = extractClientName(events[newIndex].title);
      setEmailSearchQuery(clientName);
    }
  };

  const goToPrevEvent = () => {
    if (currentEventIndex > 0) {
      const newIndex = currentEventIndex - 1;
      setCurrentEventIndex(newIndex);
      setEmailThreads([]);
      setSelectedThread(null);
      // Auto-search for new event
      const clientName = extractClientName(events[newIndex].title);
      setEmailSearchQuery(clientName);
    }
  };

  // Extract client name from event title (basic extraction)
  const extractClientName = (title) => {
    // Remove common prefixes and extract what looks like a name
    let name = title
      .replace(/^(charter|lunch|transfert|transfer|seabob|jetski|jet-ski)\s*/i, '')
      .replace(/\s*-\s*\d+.*$/, '') // Remove dates
      .replace(/\s*\(.*\)/, '') // Remove parentheses
      .trim();

    // If the name has special characters, just take the first part
    if (name.includes('@')) {
      name = name.split('@')[0];
    }

    return name || title;
  };

  const searchEmails = async () => {
    if (!emailSearchQuery.trim()) {
      message.warning('Entrez un nom de client à rechercher');
      return;
    }

    setSearchingEmails(true);
    setSelectedThread(null);

    try {
      console.log('Searching emails for:', emailSearchQuery);
      const threads = await searchClientThreads(emailSearchQuery, 10);
      console.log('Found threads:', threads);
      setEmailThreads(threads);

      if (threads.length === 0) {
        message.info('Aucun email trouvé pour cette recherche');
      }
    } catch (error) {
      console.error('Error searching emails:', error);
      message.error(`Erreur recherche emails: ${error.message}`);
    } finally {
      setSearchingEmails(false);
    }
  };

  const selectThread = (thread) => {
    setSelectedThread(thread);
  };

  const summary = getDaySummary(events);

  // Get event type from title
  const getEventType = (title) => {
    const t = title.toLowerCase();
    if (t.includes('charter') || t.includes('yacht') || t.includes('bateau')) return 'Charter';
    if (t.includes('lunch') || t.includes('dejeuner') || t.includes('déjeuner')) return 'Lunch';
    if (t.includes('transfert') || t.includes('transfer')) return 'Transfert';
    if (t.includes('seabob')) return 'Seabob';
    if (t.includes('jetski') || t.includes('jet-ski') || t.includes('jet ski')) return 'Jet-ski';
    return ''; // Leave blank for "Autre" - user will fill in captain/sailor info
  };

  // Clean HTML from description text
  const cleanHtml = (text) => {
    if (!text) return '';
    return text
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .trim();
  };

  // Export to PDF
  const exportToPDF = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const margin = 20;
      let yPos = 20;

      // Date only
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(selectedDate.format('dddd D MMMM YYYY').toUpperCase(), pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Sort events: jet-ski at the end
      const sortedEvents = [...events].sort((a, b) => {
        const aIsJetski = getEventType(a.title) === 'Jet-ski';
        const bIsJetski = getEventType(b.title) === 'Jet-ski';
        if (aIsJetski && !bIsJetski) return 1;
        if (!aIsJetski && bIsJetski) return -1;
        return 0;
      });

      // Events
      sortedEvents.forEach((event, index) => {
        // Check if we need a new page
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        const eventType = getEventType(event.title);

        // Event box
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);

        // Crew assignment (first, in red, no prefix)
        const assignedCrew = eventCrewAssignments[event.id] || [];
        if (assignedCrew.length > 0) {
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(200, 0, 0); // Red
          const crewNames = assignedCrew.map(id => getCrewName(id).toUpperCase()).join(', ');
          doc.text(crewNames, margin, yPos);
          doc.setTextColor(0, 0, 0);
          yPos += 6;
        }

        // Event type tag (only if not empty)
        if (eventType) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text(`[${eventType}]`, margin, yPos);
          yPos += 6;
        }

        // Title
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(event.title, margin, yPos);
        yPos += 6;

        // Transfer details (for SXM/SBH/AXA transfers) - in green
        const transferDetails = eventTransferDetails[event.id];
        if (isTransferEvent(event.title) && transferDetails) {
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 128, 0); // Green

          const transferParts = [];
          if (transferDetails.hotel) transferParts.push(`Hôtel/Butler: ${transferDetails.hotel}`);
          if (transferDetails.transport) transferParts.push(`Transport: ${transferDetails.transport}`);
          if (transferDetails.flightTime) transferParts.push(`Heure vol: ${transferDetails.flightTime}`);
          if (transferDetails.flightNumber) transferParts.push(`Vol: ${transferDetails.flightNumber}`);

          if (transferParts.length > 0) {
            const transferText = transferParts.join(' | ');
            const transferLines = doc.splitTextToSize(transferText, pageWidth - 2 * margin);
            transferLines.forEach(line => {
              if (yPos > 270) {
                doc.addPage();
                yPos = 20;
              }
              doc.text(line, margin, yPos);
              yPos += 4;
            });
          }
          doc.setTextColor(0, 0, 0);
        }

        // Description - in green
        if (event.description) {
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 128, 0); // Green
          const cleanDesc = cleanHtml(event.description);
          const descLines = doc.splitTextToSize(cleanDesc.substring(0, 200), pageWidth - 2 * margin);
          descLines.forEach(line => {
            if (yPos > 270) {
              doc.addPage();
              yPos = 20;
            }
            doc.text(line, margin, yPos);
            yPos += 4;
          });
          doc.setTextColor(0, 0, 0);
        }

        // Location (skip "Bureau") - in green
        if (event.location && event.location.toLowerCase() !== 'bureau') {
          doc.setFontSize(9);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(0, 128, 0); // Green
          doc.text(`Lieu: ${event.location}`, margin, yPos);
          doc.setTextColor(0, 0, 0);
          yPos += 5;
        }

        // Notes - in green
        const note = eventNotes[event.id];
        if (note) {
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 128, 0); // Green
          doc.text('Notes:', margin, yPos);
          yPos += 4;
          const noteLines = doc.splitTextToSize(note, pageWidth - 2 * margin);
          noteLines.forEach(line => {
            if (yPos > 270) {
              doc.addPage();
              yPos = 20;
            }
            doc.text(line, margin, yPos);
            yPos += 4;
          });
          doc.setTextColor(0, 0, 0);
        }

        // Separator
        yPos += 5;
        doc.setDrawColor(220, 220, 220);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;
      });

      // Save
      doc.save(`agenda_${selectedDate.format('YYYY-MM-DD')}.pdf`);
      message.success('PDF exporté avec succès !');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      message.error('Erreur lors de l\'export PDF');
    } finally {
      setExporting(false);
    }
  };

  // Export to DOCX
  const exportToDOCX = async () => {
    setExporting(true);
    try {
      const children = [];

      // Date only
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: selectedDate.format('dddd D MMMM YYYY').toUpperCase(),
              bold: true,
              size: 28,
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Sort events: jet-ski at the end
      const sortedEvents = [...events].sort((a, b) => {
        const aIsJetski = getEventType(a.title) === 'Jet-ski';
        const bIsJetski = getEventType(b.title) === 'Jet-ski';
        if (aIsJetski && !bIsJetski) return 1;
        if (!aIsJetski && bIsJetski) return -1;
        return 0;
      });

      // Events
      sortedEvents.forEach((event, index) => {
        const eventType = getEventType(event.title);

        // Crew assignment (first, in red, no prefix)
        const assignedCrew = eventCrewAssignments[event.id] || [];
        if (assignedCrew.length > 0) {
          const crewNames = assignedCrew.map(id => getCrewName(id).toUpperCase()).join(', ');
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: crewNames,
                  bold: true,
                  size: 22,
                  color: 'C80000', // Red
                }),
              ],
              spacing: { before: 300 },
            })
          );
        }

        // Event type tag (only if not empty)
        if (eventType) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `[${eventType}]`,
                  size: 18,
                  color: '666666',
                }),
              ],
              spacing: { before: assignedCrew.length > 0 ? 50 : 300 },
            })
          );
        }

        // Title
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: event.title,
                bold: true,
                size: 26,
              }),
            ],
          })
        );

        // Transfer details (for SXM/SBH/AXA transfers) - in green
        const transferDetails = eventTransferDetails[event.id];
        if (isTransferEvent(event.title) && transferDetails) {
          const transferParts = [];
          if (transferDetails.hotel) transferParts.push(`Hôtel/Butler: ${transferDetails.hotel}`);
          if (transferDetails.transport) transferParts.push(`Transport: ${transferDetails.transport}`);
          if (transferDetails.flightTime) transferParts.push(`Heure vol: ${transferDetails.flightTime}`);
          if (transferDetails.flightNumber) transferParts.push(`Vol: ${transferDetails.flightNumber}`);

          if (transferParts.length > 0) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: transferParts.join(' | '),
                    size: 20,
                    color: '008000', // Green
                  }),
                ],
                spacing: { before: 50 },
              })
            );
          }
        }

        // Description - in green
        if (event.description) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: cleanHtml(event.description),
                  size: 20,
                  color: '008000', // Green
                }),
              ],
              spacing: { before: 100 },
            })
          );
        }

        // Location (skip "Bureau") - in green
        if (event.location && event.location.toLowerCase() !== 'bureau') {
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Lieu: ${event.location}`,
                  italics: true,
                  size: 20,
                  color: '008000', // Green
                }),
              ],
            })
          );
        }

        // Notes - in green
        const note = eventNotes[event.id];
        if (note) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Notes: ',
                  bold: true,
                  size: 20,
                  color: '008000', // Green
                }),
                new TextRun({
                  text: note,
                  size: 20,
                  color: '008000', // Green
                }),
              ],
              spacing: { before: 100 },
            })
          );
        }

        // Separator
        children.push(
          new Paragraph({
            border: {
              bottom: {
                color: 'CCCCCC',
                space: 1,
                style: BorderStyle.SINGLE,
                size: 6,
              },
            },
            spacing: { after: 200 },
          })
        );
      });

      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `agenda_${selectedDate.format('YYYY-MM-DD')}.docx`);
      message.success('DOCX exporté avec succès !');
    } catch (error) {
      console.error('Error exporting DOCX:', error);
      message.error('Erreur lors de l\'export DOCX');
    } finally {
      setExporting(false);
    }
  };

  // Not authenticated view
  if (!authenticated) {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
            <div style={styles.headerIcon}>
              <CalendarOutlined />
            </div>
            <h1 style={styles.headerTitle}>Agenda du Jour</h1>
          </div>
        </div>

        <Card style={{ maxWidth: 500, margin: '100px auto', textAlign: 'center' }}>
          <CalendarOutlined style={{ fontSize: 64, color: colors.textMuted, marginBottom: spacing.lg }} />
          <h2>Connexion Google requise</h2>
          <p style={{ color: colors.textMuted, marginBottom: spacing.xxl }}>
            Connectez-vous à Google pour accéder à votre calendrier et éditer l'agenda du jour.
          </p>
          <Button
            type="primary"
            size="large"
            icon={<GoogleOutlined />}
            onClick={handleConnectGoogle}
            style={styles.primaryBtn}
          >
            Se connecter avec Google
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.lg }}>
          <div style={styles.headerIcon}>
            <CalendarOutlined />
          </div>
          <div>
            <h1 style={styles.headerTitle}>Agenda du Jour</h1>
            <span style={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}>
              {events.length} événement{events.length > 1 ? 's' : ''}
            </span>
          </div>
          {/* Event type counts */}
          {events.length > 0 && (() => {
            const counts = {};
            events.forEach(e => {
              const colorType = getEventColorType(e.title);
              let label = '';
              if (colorType.startsWith('full_day')) label = 'Full Day';
              else if (colorType.startsWith('half_day')) label = 'Half Day';
              else if (colorType.startsWith('trf') || colorType === 'Transfert') label = 'Transfert';
              else if (colorType === 'sunset') label = 'Sunset';
              else if (colorType === 'Jet-ski') label = 'Jet-ski';
              else if (colorType === 'option') label = 'Option';
              else if (colorType === 'Charter') label = 'Charter';
              else if (colorType === 'Lunch') label = 'Lunch';
              else if (colorType === 'Seabob') label = 'Seabob';
              else label = 'Autre';
              counts[label] = (counts[label] || 0) + 1;
            });
            return (
              <div style={{ display: 'flex', gap: spacing.sm, marginLeft: spacing.md }}>
                {Object.entries(counts).map(([label, count]) => {
                  let bgColor = '#e0e0e0';
                  if (label === 'Full Day') bgColor = '#F4511E';
                  else if (label === 'Half Day') bgColor = '#33B679';
                  else if (label === 'Transfert') bgColor = '#FDD663';
                  else if (label === 'Sunset') bgColor = '#9AA0A6';
                  else if (label === 'Jet-ski') bgColor = '#c2185b';
                  else if (label === 'Option') bgColor = '#8AB4F8';
                  else if (label === 'Charter') bgColor = '#1976d2';
                  else if (label === 'Lunch') bgColor = '#f57c00';
                  else if (label === 'Seabob') bgColor = '#00838f';
                  const textColor = label === 'Transfert' ? '#333' : '#fff';
                  return (
                    <Tag key={label} style={{ background: bgColor, color: textColor, border: 'none', fontWeight: 600 }}>
                      {count} {label}
                    </Tag>
                  );
                })}
              </div>
            );
          })()}
        </div>

        <Space size="middle">
          <Select
            value={selectedCalendar}
            onChange={setSelectedCalendar}
            style={{ width: 250 }}
            placeholder="Sélectionner un calendrier"
            options={[
              { value: 'primary', label: '📅 Calendrier principal' },
              ...calendars.map(cal => ({
                value: cal.id,
                label: cal.summary,
              }))
            ]}
          />
          <DatePicker
            value={selectedDate}
            onChange={setSelectedDate}
            format="DD/MM/YYYY"
            allowClear={false}
            size="large"
            style={{ borderRadius: radius.lg }}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={loadEvents}
            loading={loading}
            style={styles.secondaryBtn}
          >
            Actualiser
          </Button>
          <Button
            icon={<SyncOutlined />}
            onClick={() => {
              clearTokens();
              window.location.href = getGoogleAuthUrl();
            }}
            style={styles.secondaryBtn}
          >
            Sync Google
          </Button>
          <Divider type="vertical" style={{ height: 32 }} />
          <Button
            icon={<FilePdfOutlined />}
            onClick={exportToPDF}
            loading={exporting}
            disabled={events.length === 0}
            style={styles.primaryBtn}
          >
            PDF
          </Button>
          <Button
            icon={<FileWordOutlined />}
            onClick={exportToDOCX}
            loading={exporting}
            disabled={events.length === 0}
            style={styles.secondaryBtn}
          >
            DOCX
          </Button>
          <Button
            icon={<MailOutlined />}
            onClick={enterReviewMode}
            disabled={events.length === 0}
            style={styles.secondaryBtn}
          >
            Revue Emails
          </Button>
        </Space>
      </div>

      {/* Main Content */}
      <div>
        {/* Events List or Review Mode */}
        {reviewMode ? (
          // Review Mode View
          <div style={styles.reviewContainer}>
            {/* Left: Current Event Card */}
            {(() => {
              const currentEvent = events[currentEventIndex];
              const colorType = currentEvent ? getEventColorType(currentEvent.title) : 'Autre';
              const borderColor = (eventTypeColors[colorType] || eventTypeColors['Autre']).border;

              return (
                <div style={styles.reviewCard}>
                  {/* Navigation */}
                  <div style={styles.reviewNavigation}>
                    <Button
                      icon={<LeftOutlined />}
                      onClick={goToPrevEvent}
                      disabled={currentEventIndex === 0}
                      style={styles.secondaryBtn}
                    >
                      Précédent
                    </Button>
                    <span style={styles.reviewCounter}>
                      {currentEventIndex + 1} / {events.length}
                    </span>
                    <Button
                      icon={<RightOutlined />}
                      onClick={goToNextEvent}
                      disabled={currentEventIndex === events.length - 1}
                      style={styles.secondaryBtn}
                    >
                      Suivant
                    </Button>
                  </div>

                  {/* Current Event */}
                  {currentEvent && (() => {
                    const event = currentEvent;
                    const eventType = getEventType(event.title);
                    const typeColor = eventTypeColors[colorType] || eventTypeColors['Autre'];

                    return (
                      <>
                        <div style={{
                          ...styles.eventHeader,
                          background: typeColor.border,
                          borderBottom: 'none',
                        }}>
                          <div style={{ ...styles.eventTime, color: '#fff' }}>
                            <ClockCircleOutlined />
                            {event.isAllDay ? 'Journée' : event.startTime}
                          </div>
                          {eventType && (
                            <Tag style={{ background: 'rgba(255,255,255,0.9)', color: typeColor.border, border: 'none', borderRadius: radius.md, fontWeight: 600 }}>
                              {eventType}
                            </Tag>
                          )}
                        </div>
                    <div style={styles.eventBody}>
                      <h3 style={styles.eventTitle}>{event.title}</h3>
                      {event.description && (
                        <p style={styles.eventDescription}>{cleanHtml(event.description)}</p>
                      )}
                      {event.location && event.location.toLowerCase() !== 'bureau' && (
                        <div style={styles.eventLocation}>
                          <EnvironmentOutlined />
                          {event.location}
                        </div>
                      )}
                      <div style={styles.notesSection}>
                        <div style={styles.notesLabel}>
                          <EditOutlined />
                          Vos notes
                        </div>
                        <TextArea
                          placeholder="Ajoutez vos notes ici..."
                          value={eventNotes[event.id] || ''}
                          onChange={(e) => handleNoteChange(event.id, e.target.value)}
                          autoSize={{ minRows: 3, maxRows: 8 }}
                          style={{ borderRadius: radius.md }}
                        />
                      </div>
                    </div>
                  </>
                );
              })()}

                  {/* Exit Review Mode */}
                  <div style={{ padding: spacing.lg, borderTop: `1px solid ${colors.borderLight}` }}>
                    <Button
                      icon={<CloseOutlined />}
                      onClick={exitReviewMode}
                      style={{ ...styles.secondaryBtn, width: '100%' }}
                    >
                      Quitter le mode revue
                    </Button>
                  </div>
                </div>
              );
            })()}

            {/* Right: Email Panel */}
            <div style={styles.emailPanel}>
              {/* Email Search Header */}
              <div style={styles.emailHeader}>
                <h3 style={{ margin: 0, fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold }}>
                  <MailOutlined style={{ marginRight: spacing.sm }} />
                  Recherche Email
                </h3>
                <div style={styles.emailSearchBox}>
                  <Input
                    placeholder="Nom du client..."
                    value={emailSearchQuery}
                    onChange={(e) => setEmailSearchQuery(e.target.value)}
                    onPressEnter={searchEmails}
                    style={{ flex: 1, borderRadius: radius.md }}
                  />
                  <Button
                    icon={<SearchOutlined />}
                    onClick={searchEmails}
                    loading={searchingEmails}
                    style={styles.primaryBtn}
                  >
                    Rechercher
                  </Button>
                </div>
              </div>

              {/* Thread List */}
              {emailThreads.length > 0 && !selectedThread && (
                <div style={styles.threadList}>
                  {emailThreads.map((thread) => (
                    <div
                      key={thread.id}
                      style={styles.threadItem}
                      onClick={() => selectThread(thread)}
                    >
                      <div style={styles.threadSubject}>{thread.subject}</div>
                      <div style={styles.threadSnippet}>
                        {thread.messageCount} message(s) - {thread.snippet}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Thread Viewer */}
              {selectedThread && (
                <>
                  <div style={{ padding: spacing.md, borderBottom: `1px solid ${colors.borderLight}` }}>
                    <Button
                      size="small"
                      onClick={() => setSelectedThread(null)}
                      icon={<LeftOutlined />}
                    >
                      Retour à la liste
                    </Button>
                    <h4 style={{ margin: `${spacing.sm} 0 0 0`, fontSize: typography.fontSize.md }}>
                      {selectedThread.subject}
                    </h4>
                  </div>
                  <div style={styles.threadViewer}>
                    {selectedThread.messages.map((msg, idx) => (
                      <div key={idx} style={styles.emailMessage}>
                        <div style={styles.emailFrom}>{msg.from}</div>
                        <div style={styles.emailDate}>{msg.date}</div>
                        <div style={styles.emailBody}>
                          {msg.body || msg.snippet || 'Pas de contenu'}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Empty state */}
              {emailThreads.length === 0 && !searchingEmails && (
                <div style={{ padding: spacing.xxl, textAlign: 'center' }}>
                  <MailOutlined style={{ fontSize: 48, color: colors.textMuted, marginBottom: spacing.md }} />
                  <p style={{ color: colors.textMuted }}>
                    Recherchez des emails par nom de client pour voir les conversations associées.
                  </p>
                </div>
              )}

              {searchingEmails && (
                <div style={{ padding: spacing.xxl, textAlign: 'center' }}>
                  <Spin size="large" />
                  <p style={{ marginTop: spacing.lg, color: colors.textMuted }}>
                    Recherche en cours...
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Normal Grid View with Crew Panel on Left
          <div style={{ display: 'flex', gap: spacing.lg }}>
            {/* Left: Available Crew Panel */}
            {crewMembers.length > 0 && events.length > 0 && (() => {
              const availableCrew = getAvailableCrew();
              const captains = availableCrew.filter(c => {
                const role = (c.role || '').toLowerCase();
                return role.includes('capitaine') || role.includes('skipper');
              });
              const sailors = availableCrew.filter(c => {
                const role = (c.role || '').toLowerCase();
                return !role.includes('capitaine') && !role.includes('skipper');
              });

              return (
                <div style={{ ...styles.crewPanel, width: '200px', flexShrink: 0, position: 'sticky', top: spacing.xxl, alignSelf: 'flex-start' }}>
                  <div style={styles.crewPanelTitle}>Équipage disponible</div>
                  {availableCrew.length === 0 ? (
                    <span style={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}>
                      Tout l'équipage est assigné
                    </span>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                      {/* Capitaines */}
                      {captains.length > 0 && (
                        <div>
                          <div style={{ fontSize: typography.fontSize.xs, color: '#d48806', fontWeight: typography.fontWeight.semibold, marginBottom: spacing.xs }}>
                            Capitaines
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                            {captains.map(crew => (
                              <div key={crew.id} style={{ ...styles.crewChip, margin: 0, background: '#fffbe6', borderColor: '#ffe58f' }}>
                                <span>{crew.first_name || crew.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Marins */}
                      {sailors.length > 0 && (
                        <div>
                          <div style={{ fontSize: typography.fontSize.xs, color: '#1890ff', fontWeight: typography.fontWeight.semibold, marginBottom: spacing.xs }}>
                            Marins
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                            {sailors.map(crew => (
                              <div key={crew.id} style={{ ...styles.crewChip, margin: 0, background: '#e6f7ff', borderColor: '#91d5ff' }}>
                                <span>{crew.first_name || crew.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Right: Events Grid */}
            <div style={{ ...styles.mainContent, flex: 1 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: spacing.xxxxl, gridColumn: '1 / -1' }}>
                <Spin size="large" />
                <p style={{ marginTop: spacing.lg, color: colors.textMuted }}>
                  Chargement des événements...
                </p>
              </div>
            ) : events.length === 0 ? (
              <Card style={{ textAlign: 'center', padding: spacing.xxxxl, gridColumn: '1 / -1' }}>
                <Empty
                  description={
                    <span style={{ color: colors.textMuted }}>
                      Aucun événement pour le {selectedDate.format('DD/MM/YYYY')}
                    </span>
                  }
                />
              </Card>
            ) : (
              events.map((event) => {
                const eventType = getEventType(event.title);
                const colorType = getEventColorType(event.title);
                const typeColor = eventTypeColors[colorType] || eventTypeColors['Autre'];

                return (
                  <div key={event.id} style={styles.eventCard}>
                    {/* Event Header - Colored */}
                    <div style={{
                      ...styles.eventHeader,
                      background: typeColor.border,
                      borderBottom: 'none',
                    }}>
                      <div style={{ ...styles.eventTime, color: '#fff' }}>
                        <ClockCircleOutlined />
                        {event.isAllDay ? 'Journée' : event.startTime}
                      </div>
                      {eventType && (
                        <Tag
                          style={{
                            background: 'rgba(255,255,255,0.9)',
                            color: typeColor.border,
                            border: 'none',
                            borderRadius: radius.md,
                            fontWeight: 600,
                          }}
                        >
                          {eventType}
                        </Tag>
                      )}
                    </div>

                    {/* Crew Selector */}
                    <div style={styles.crewSelector}>
                      <span style={styles.crewSelectorLabel}>Équipage assigné</span>
                      <Select
                        mode="multiple"
                        placeholder="Sélectionner l'équipage..."
                        value={eventCrewAssignments[event.id] || []}
                        onChange={(values) => handleCrewAssignment(event.id, values)}
                        style={{ width: '100%' }}
                        optionFilterProp="children"
                      >
                        {/* Suggestion: 1 captain + 2 sailors */}
                        <Select.OptGroup label="⭐ Suggestion">
                          {getSuggestedCrew().map(crew => {
                            const role = (crew.role || '').toLowerCase();
                            const isCaptain = role.includes('capitaine') || role.includes('skipper');
                            return (
                              <Select.Option key={crew.id} value={crew.id}>
                                {isCaptain ? '👨‍✈️' : '⚓'} {crew.first_name || crew.name}
                              </Select.Option>
                            );
                          })}
                        </Select.OptGroup>
                        {/* Remaining captains */}
                        {getRemainingCrew().filter(c => {
                          const role = (c.role || '').toLowerCase();
                          return role.includes('capitaine') || role.includes('skipper');
                        }).length > 0 && (
                          <Select.OptGroup label="Autres Capitaines">
                            {getRemainingCrew().filter(c => {
                              const role = (c.role || '').toLowerCase();
                              return role.includes('capitaine') || role.includes('skipper');
                            }).map(crew => (
                              <Select.Option key={crew.id} value={crew.id}>
                                {crew.first_name || crew.name}
                              </Select.Option>
                            ))}
                          </Select.OptGroup>
                        )}
                        {/* Remaining sailors */}
                        {getRemainingCrew().filter(c => {
                          const role = (c.role || '').toLowerCase();
                          return !role.includes('capitaine') && !role.includes('skipper');
                        }).length > 0 && (
                          <Select.OptGroup label="Autres Marins">
                            {getRemainingCrew().filter(c => {
                              const role = (c.role || '').toLowerCase();
                              return !role.includes('capitaine') && !role.includes('skipper');
                            }).map(crew => (
                              <Select.Option key={crew.id} value={crew.id}>
                                {crew.first_name || crew.name}
                              </Select.Option>
                            ))}
                          </Select.OptGroup>
                        )}
                      </Select>
                    </div>

                    {/* Event Body */}
                    <div style={styles.eventBody}>
                      <h3 style={styles.eventTitle}>{event.title}</h3>

                      {event.description && (
                        <p style={styles.eventDescription}>{cleanHtml(event.description)}</p>
                      )}

                      {event.location && event.location.toLowerCase() !== 'bureau' && (
                        <div style={styles.eventLocation}>
                          <EnvironmentOutlined />
                          {event.location}
                        </div>
                      )}

                      {/* Notes Section */}
                      <div style={styles.notesSection}>
                        <div style={styles.notesLabel}>
                          <EditOutlined />
                          Vos notes (seront incluses dans l'export)
                        </div>
                        <TextArea
                          placeholder="Ajoutez vos notes ici..."
                          value={eventNotes[event.id] || ''}
                          onChange={(e) => handleNoteChange(event.id, e.target.value)}
                          autoSize={{ minRows: 2, maxRows: 6 }}
                          style={{ borderRadius: radius.md }}
                        />

                        {/* Transfer Details (only for SXM/SBH/AXA transfers) */}
                        {isTransferEvent(event.title) && (
                          <div style={{ marginTop: spacing.md, paddingTop: spacing.md, borderTop: `1px dashed ${colors.borderMedium}` }}>
                            <div style={styles.transferSectionLabel}>
                              <EnvironmentOutlined />
                              Détails Transfert
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.xs }}>
                              <Input
                                placeholder="Hôtel ou Butler..."
                                value={eventTransferDetails[event.id]?.hotel || ''}
                                onChange={(e) => handleTransferDetailChange(event.id, 'hotel', e.target.value)}
                                size="small"
                              />
                              <Select
                                placeholder={isSXMTransfer(event.title) ? "VIP Georges ?" : "Transport"}
                                value={eventTransferDetails[event.id]?.transport || undefined}
                                onChange={(value) => handleTransferDetailChange(event.id, 'transport', value)}
                                style={{ width: '100%' }}
                                size="small"
                                allowClear
                              >
                                {isSXMTransfer(event.title) && (
                                  <Select.Option value="VIP Georges">VIP Georges</Select.Option>
                                )}
                                <Select.Option value="Standard Georges">Standard Georges</Select.Option>
                                <Select.Option value="Autre">Autre</Select.Option>
                              </Select>
                              <Input
                                placeholder="Heure vol (14:30)"
                                value={eventTransferDetails[event.id]?.flightTime || ''}
                                onChange={(e) => handleTransferDetailChange(event.id, 'flightTime', e.target.value)}
                                size="small"
                              />
                              <Input
                                placeholder="N° vol (AF123)"
                                value={eventTransferDetails[event.id]?.flightNumber || ''}
                                onChange={(e) => handleTransferDetailChange(event.id, 'flightNumber', e.target.value)}
                                size="small"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AgendaDayEditor;
